import {
  type BigDecimal,
  createBigDecimal,
  plus,
  toStringBigDecimal,
} from '@0xtorch/big-decimal'
import type { FiatCurrency } from '@0xtorch/core'
/**
 * 1 会計期間分の損益計算を段階的に実行する。
 * action 生成、transaction 保存、WACB 再計算、期末 state 保存を順に進め、不要な DB 再読込と debug I/O を避ける。
 * WACB は単価集計と取引再生成の二段階で処理し、final row だけをバッチ保存する。
 */
import type {
  AssetBalance,
  AssetBorrow,
  CrossActionBundle,
  Loan,
  Transaction,
  calculateTransactionPlByWacb,
  createAssetBalancesForWacb,
  createTransactions,
} from '@pkg/accounting'
import type {
  AccountingPeriod,
  AccountingPeriodComputedSummary,
  Portfolio,
} from '@pkg/database-portfolio'
import {
  type PeriodPlTotals,
  addPeriodPlTransfer,
  createPeriodPlTotals,
  insertAccountingTransactions,
  queryActionsForGenerateAccountingTransactionByRange,
  resetAccountingPeriodData,
  saveAccountingPeriodData,
  selectAssetBalances,
  selectAssetBorrows,
  selectCrossActionBundles,
  selectLoans,
  selectNftAcquiredTimestamps,
} from '@pkg/database-portfolio'
import {
  type AccountingProfiler,
  noopAccountingProfiler,
} from './profiling/accountingProfiler'
import type {
  OnUpdateCalculationProgressCallback,
  RunToDatabase,
} from './types'

const defaultMaxOnceItemCount = 1_000

type AccountingPeriodSummaryAccumulator = {
  readonly actionIds: Set<string>
  readonly hasInsufficientAmountTxActionIds: Set<string>
  readonly hasUnknownPriceTxActionIds: Set<string>
  readonly actionTotalPls: Map<string, BigDecimal>
  readonly periodPlTotals: PeriodPlTotals
}

type RecordSummaryTransactionsParameters = {
  readonly accumulator: AccountingPeriodSummaryAccumulator
  readonly period: AccountingPeriod
  readonly transactions: readonly Transaction[]
}

type AssetBalanceListItem = {
  readonly asset: string
  readonly balances: readonly AssetBalance[]
}

type AssetBorrowListItem = {
  readonly asset: string
  readonly borrows: readonly AssetBorrow[]
}

/**
 * BigDecimal を別 object として複製する。
 * @param value 複製対象の BigDecimal。
 * @returns 同じ数値を表す新しい BigDecimal object。
 */
const cloneBigDecimal = (value: BigDecimal): BigDecimal => ({
  value: value.value,
  decimals: value.decimals,
})

/**
 * AssetBalance を deep clone する。
 * @param balance 複製対象の asset balance。
 * @returns amount と value も複製した asset balance。
 */
const cloneAssetBalance = (balance: AssetBalance): AssetBalance => ({
  amount: cloneBigDecimal(balance.amount),
  value: cloneBigDecimal(balance.value),
})

/**
 * AssetBorrow を deep clone する。
 * @param borrow 複製対象の asset borrow。
 * @returns amount、value、borrowedAmount、borrowedValue を複製した asset borrow。
 */
const cloneAssetBorrow = (borrow: AssetBorrow): AssetBorrow => ({
  amount: cloneBigDecimal(borrow.amount),
  value: cloneBigDecimal(borrow.value),
  borrowedAmount: cloneBigDecimal(borrow.borrowedAmount),
  borrowedValue: cloneBigDecimal(borrow.borrowedValue),
})

/**
 * asset balance list を deep clone する。
 * @param list DB から読んだ asset balance list。
 * @returns 各 balance object まで複製した list。
 */
const cloneAssetBalanceList = (
  list: readonly AssetBalanceListItem[],
): AssetBalanceListItem[] =>
  list.map(({ asset, balances }) => ({
    asset,
    balances: balances.map(cloneAssetBalance),
  }))

/**
 * asset borrow list を deep clone する。
 * @param list DB から読んだ asset borrow list。
 * @returns 各 borrow object まで複製した list。
 */
const cloneAssetBorrowList = (
  list: readonly AssetBorrowListItem[],
): AssetBorrowListItem[] =>
  list.map(({ asset, borrows }) => ({
    asset,
    borrows: borrows.map(cloneAssetBorrow),
  }))

/**
 * asset balance list を計算用 Map に変換する。
 * @param list asset と balances の list。
 * @returns asset key から balances を引く Map。
 */
const createAssetBalanceMap = (
  list: readonly AssetBalanceListItem[],
): Map<string, readonly AssetBalance[]> => {
  const map = new Map<string, readonly AssetBalance[]>()
  for (const { asset, balances } of list) {
    map.set(asset, balances)
  }
  return map
}

/**
 * asset borrow list を計算用 Map に変換する。
 * @param list asset と borrows の list。
 * @returns asset key から borrows を引く Map。
 */
const createAssetBorrowMap = (
  list: readonly AssetBorrowListItem[],
): Map<string, readonly AssetBorrow[]> => {
  const map = new Map<string, readonly AssetBorrow[]>()
  for (const { asset, borrows } of list) {
    map.set(asset, borrows)
  }
  return map
}

/**
 * period summary 用の可変 accumulator を初期化する。
 * @returns action/source 集計をバッチ単位で足し込むための accumulator。
 */
const createAccountingPeriodSummaryAccumulator =
  (): AccountingPeriodSummaryAccumulator => ({
    actionIds: new Set<string>(),
    hasInsufficientAmountTxActionIds: new Set<string>(),
    hasUnknownPriceTxActionIds: new Set<string>(),
    actionTotalPls: new Map<string, BigDecimal>(),
    periodPlTotals: createPeriodPlTotals(),
  })

/**
 * final transaction 群を summary accumulator へ反映する。
 * @param params accumulator と final transaction 群を受け取る。
 */
const recordSummaryTransactions = ({
  accumulator,
  transactions,
  period,
}: RecordSummaryTransactionsParameters): void => {
  for (const transaction of transactions) {
    if (transaction.category === 'insufficient') {
      accumulator.hasInsufficientAmountTxActionIds.add(transaction.actionId)
    }

    if (!accumulator.actionIds.has(transaction.actionId)) {
      throw new Error(
        `actionId not found in summary accumulator: ${transaction.actionId}`,
      )
    }

    for (const transfer of transaction.ins) {
      if (
        transaction.timestamp >= period.start &&
        transaction.timestamp <= period.end
      ) {
        addPeriodPlTransfer({ totals: accumulator.periodPlTotals, transfer })
      }
      if (transfer.zeroCompleted) {
        accumulator.hasUnknownPriceTxActionIds.add(transaction.actionId)
      }
      accumulator.actionTotalPls.set(
        transaction.actionId,
        plus(
          accumulator.actionTotalPls.get(transaction.actionId) ??
            createBigDecimal(0n),
          transfer.totalPl,
        ),
      )
    }

    for (const transfer of transaction.outs) {
      if (
        transaction.timestamp >= period.start &&
        transaction.timestamp <= period.end
      ) {
        addPeriodPlTransfer({ totals: accumulator.periodPlTotals, transfer })
      }
      if (transfer.zeroCompleted) {
        accumulator.hasUnknownPriceTxActionIds.add(transaction.actionId)
      }
      accumulator.actionTotalPls.set(
        transaction.actionId,
        plus(
          accumulator.actionTotalPls.get(transaction.actionId) ??
            createBigDecimal(0n),
          transfer.totalPl,
        ),
      )
    }
  }
}

/**
 * accumulator を `saveAccountingPeriodData` 用の summary 型へ変換する。
 * @param params 変換対象の accumulator を受け取る。
 * @returns action/source 更新に必要な最終 summary。
 */
const buildAccountingPeriodComputedSummary = ({
  accumulator,
}: {
  readonly accumulator: AccountingPeriodSummaryAccumulator
}): AccountingPeriodComputedSummary => ({
  periodPlTotals: accumulator.periodPlTotals,
  actionIds: [...accumulator.actionIds],
  hasInsufficientAmountTxActionIds: [
    ...accumulator.hasInsufficientAmountTxActionIds,
  ],
  hasUnknownPriceTxActionIds: [...accumulator.hasUnknownPriceTxActionIds],
  actionTotalPls: new Map(
    [...accumulator.actionTotalPls.entries()].map(([actionId, totalPl]) => [
      actionId,
      toStringBigDecimal(totalPl),
    ]),
  ),
})

type GenerateAccountingTransactionListOfAccountingPeriodParameters = {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
  portfolio: Portfolio
  accountingPeriod: AccountingPeriod
  fiats: readonly FiatCurrency[]
  createTransactions: (
    parameters: Parameters<typeof createTransactions>['0'],
  ) => Promise<ReturnType<typeof createTransactions>>
  createAssetBalancesForWacb: (
    parameters: Parameters<typeof createAssetBalancesForWacb>['0'],
  ) => Promise<ReturnType<typeof createAssetBalancesForWacb>>
  calculateTransactionPlByWacb: (
    parameters: Parameters<typeof calculateTransactionPlByWacb>['0'],
  ) => Promise<ReturnType<typeof calculateTransactionPlByWacb>>
  onUpdateProgress?: OnUpdateCalculationProgressCallback
  profiler?: AccountingProfiler
  maxOnceItemCount?: number
  transactionFlushItemCount?: number
}

/**
 * 1 会計期間分の会計 transaction と損益を生成し、最終 state を DB へ保存する。
 * @param params DB wrapper、portfolio、対象 period、optional profiler を受け取る。
 */
export const generateAccountingTransactionListOfAccountingPeriod = async ({
  readFromDatabase,
  writeToDatabase,
  portfolio,
  accountingPeriod,
  fiats,
  createTransactions,
  createAssetBalancesForWacb,
  calculateTransactionPlByWacb,
  onUpdateProgress,
  profiler,
  maxOnceItemCount,
  transactionFlushItemCount,
}: GenerateAccountingTransactionListOfAccountingPeriodParameters): Promise<void> => {
  const effectiveProfiler = profiler ?? noopAccountingProfiler
  const effectiveMaxOnceItemCount = maxOnceItemCount ?? defaultMaxOnceItemCount
  const effectiveTransactionFlushItemCount =
    transactionFlushItemCount ?? effectiveMaxOnceItemCount
  const profileMetadata = {
    periodId: accountingPeriod.id,
    costBasis: portfolio.costBasis,
    maxOnceItemCount: effectiveMaxOnceItemCount,
    transactionFlushItemCount: effectiveTransactionFlushItemCount,
  }
  const profiledReadFromDatabase: RunToDatabase = async (run) =>
    await effectiveProfiler.measure(
      'db.read.total',
      async () => await readFromDatabase(run),
    )
  const profiledWriteToDatabase: RunToDatabase = async (run) =>
    await effectiveProfiler.measure(
      'db.write.total',
      async () => await writeToDatabase(run),
    )

  await effectiveProfiler.measure(
    'period.total',
    async () => {
      if (onUpdateProgress) {
        onUpdateProgress({
          range: accountingPeriod.end - accountingPeriod.start + 1,
          synced: 0,
        })
      }

      await effectiveProfiler.measure(
        'period.resetAccountingPeriodData',
        async () =>
          await profiledWriteToDatabase((database) =>
            resetAccountingPeriodData({
              database,
              accountingPeriodId: accountingPeriod.id,
            }),
          ),
        profileMetadata,
      )

      const previousLoans = await effectiveProfiler.measure(
        'period.selectPreviousLoans',
        async () =>
          await profiledReadFromDatabase((database) =>
            selectLoans({
              database,
              accountingPeriodId: accountingPeriod.id - 1,
            }),
          ),
        profileMetadata,
      )
      const previousCrossActionBundles = await effectiveProfiler.measure(
        'period.selectPreviousCrossActionBundles',
        async () =>
          await profiledReadFromDatabase((database) =>
            selectCrossActionBundles({
              database,
              accountingPeriodId: accountingPeriod.id - 1,
              fiatCurrencyList: fiats,
            }),
          ),
        profileMetadata,
      )
      const previousAssetBalances = await effectiveProfiler.measure(
        'period.selectPreviousAssetBalances',
        async () =>
          await profiledReadFromDatabase((database) =>
            selectAssetBalances({
              database,
              accountingPeriodId: accountingPeriod.id - 1,
            }),
          ),
        profileMetadata,
      )
      const previousAssetBorrows = await effectiveProfiler.measure(
        'period.selectPreviousAssetBorrows',
        async () =>
          await profiledReadFromDatabase((database) =>
            selectAssetBorrows({
              database,
              accountingPeriodId: accountingPeriod.id - 1,
            }),
          ),
        profileMetadata,
      )
      const previousNftAcquiredTimestamps = await effectiveProfiler.measure(
        'period.selectPreviousNftAcquiredTimestamps',
        async () =>
          await profiledReadFromDatabase((database) =>
            selectNftAcquiredTimestamps({
              database,
              accountingPeriodId: accountingPeriod.id - 1,
            }),
          ),
        profileMetadata,
      )

      const previousAssetBalancesForGeneration = cloneAssetBalanceList(
        previousAssetBalances,
      )
      const previousAssetBorrowsForGeneration =
        cloneAssetBorrowList(previousAssetBorrows)
      const previousAssetBalancesForWacb = cloneAssetBalanceList(
        previousAssetBalances,
      )
      const previousAssetBorrowsForWacb =
        cloneAssetBorrowList(previousAssetBorrows)

      let loans = new Map<string, Loan>(
        previousLoans.map((loan) => [loan.id, loan]),
      )
      let crossActionBundles = new Map<string, CrossActionBundle>(
        previousCrossActionBundles.map((crossActionBundle) => [
          crossActionBundle.id,
          crossActionBundle,
        ]),
      )
      let assetBalances = createAssetBalanceMap(
        previousAssetBalancesForGeneration,
      )
      let assetBorrows = createAssetBorrowMap(previousAssetBorrowsForGeneration)
      let nftAcquiredTimestamps = new Map(previousNftAcquiredTimestamps)
      let usedBalances = new Map<string, AssetBalance>()
      const summaryAccumulator = createAccountingPeriodSummaryAccumulator()
      const transactionWriteBuffer: Transaction[] = []
      let transactionFlushIndex = 0

      const flushTransactions = async ({
        eventName,
        transactions,
        metadata,
      }: {
        readonly eventName:
          | 'period.actionBatch.insertAccountingTransactions'
          | 'period.wacb.insertAccountingTransactions'
        readonly transactions: readonly Transaction[]
        readonly metadata: Record<string, unknown>
      }): Promise<void> => {
        if (transactions.length === 0) {
          return
        }

        await effectiveProfiler.measure(
          eventName,
          async () =>
            await profiledWriteToDatabase((database) =>
              insertAccountingTransactions({
                database,
                accountingPeriodId: accountingPeriod.id,
                transactions,
                deleteExistingTransfers: false,
              }),
            ),
          metadata,
        )
      }

      /**
       * アクションをバッチ単位で生成し、取引を保持せず呼び出し元へ渡す。
       * @param params バッチ処理と生成段階の進捗通知の有無。
       * @returns 全バッチの処理完了を表す Promise。
       */
      const runActionBatches = async ({
        onTransactions,
        reportGenerationProgress,
      }: {
        readonly onTransactions: (params: {
          readonly transactions: Transaction[]
          readonly batchMetadata: Record<string, unknown>
        }) => Promise<void>
        readonly reportGenerationProgress: boolean
      }): Promise<void> => {
        let from = accountingPeriod.start
        let after: Parameters<
          typeof queryActionsForGenerateAccountingTransactionByRange
        >[0]['after']
        let actionBatchIndex = 0
        while (true) {
          const batchMetadata = {
            ...profileMetadata,
            batchIndex: actionBatchIndex,
            from,
            to: accountingPeriod.end,
          }
          const transactions: Transaction[] = []
          const actions = await effectiveProfiler.measure(
            'period.actionBatch.query',
            async () =>
              await profiledReadFromDatabase((database) =>
                queryActionsForGenerateAccountingTransactionByRange({
                  database,
                  from,
                  after,
                  to: accountingPeriod.end,
                  limit: effectiveMaxOnceItemCount,
                  fiats,
                }),
              ),
            batchMetadata,
          )
          /** 取引生成による Action の変更から、DB の並び順に基づくカーソルを保護する。 */
          const lastAction = actions.at(-1)
          const nextAfter = lastAction
            ? {
                timestamp: lastAction.timestamp,
                crossType: lastAction.crossType,
                source: lastAction.source,
                order: lastAction.order,
              }
            : undefined
          await effectiveProfiler.measure(
            'period.actionBatch.createTransactions',
            async () => {
              for (const action of actions) {
                const actionId = `${action.source}_${action.order}`
                summaryAccumulator.actionIds.add(actionId)
                const result = await createTransactions({
                  actionId,
                  action,
                  loans,
                  crossActionBundles,
                  assetBalances,
                  assetBorrows,
                  usedBalances,
                  nftAcquiredTimestamps,
                  costBasis: portfolio.costBasis,
                  isLocked: false,
                  handleLoanAsTrade: portfolio.handleLoanAsTrade,
                  handleReplaceAsTrade: portfolio.handleReplaceAsTrade,
                  handleTransferAsLoss: portfolio.needPeriodEndEvaluation,
                  use5PercentRule: portfolio.use5PercentRule,
                })
                transactions.push(...result.transactions)
                loans = result.loans
                crossActionBundles = result.crossActionBundles
                assetBalances = result.assetBalances
                assetBorrows = result.assetBorrows
                usedBalances = result.usedBalances
                nftAcquiredTimestamps = result.nftAcquiredTimestamps

                if (reportGenerationProgress && onUpdateProgress) {
                  onUpdateProgress({
                    synced:
                      (action.timestamp - accountingPeriod.start) /
                      (portfolio.costBasis === 'WACB' ? 2 : 1),
                  })
                }
              }
            },
            {
              ...batchMetadata,
              actionCount: actions.length,
            },
          )
          await onTransactions({ transactions, batchMetadata })
          if (actions.length < effectiveMaxOnceItemCount) {
            break
          }
          after = nextAfter
          from = nextAfter?.timestamp ?? from
          actionBatchIndex += 1
        }
      }
      await runActionBatches({
        reportGenerationProgress: true,
        onTransactions: async ({ transactions, batchMetadata }) => {
          if (portfolio.costBasis === 'WACB') return
          recordSummaryTransactions({
            accumulator: summaryAccumulator,
            period: accountingPeriod,
            transactions,
          })
          transactionWriteBuffer.push(...transactions)
          if (
            transactionWriteBuffer.length >= effectiveTransactionFlushItemCount
          ) {
            await flushTransactions({
              eventName: 'period.actionBatch.insertAccountingTransactions',
              transactions: transactionWriteBuffer,
              metadata: { ...batchMetadata, flushIndex: transactionFlushIndex },
            })
            transactionWriteBuffer.length = 0
            transactionFlushIndex += 1
          }
        },
      })

      let assetBalanceList: {
        readonly asset: string
        readonly balances: readonly AssetBalance[]
      }[]
      let assetBorrowList: {
        readonly asset: string
        readonly borrows: readonly AssetBorrow[]
      }[]
      if (portfolio.costBasis === 'WACB') {
        let assetBalancesForWacb = new Map<string, BigDecimal>()
        for (const assetBalance of previousAssetBalancesForWacb) {
          const totalAmount = assetBalance.balances.reduce(
            (total, balance) => plus(total, balance.amount),
            createBigDecimal(0n),
          )
          assetBalancesForWacb.set(assetBalance.asset, totalAmount)
        }
        let assetBorrowsForWacb = createAssetBorrowMap(
          previousAssetBorrowsForWacb,
        )
        let totalBalances = await effectiveProfiler.measure(
          'period.wacb.createAssetBalancesForWacb',
          async () =>
            await createAssetBalancesForWacb({
              assetBalances,
              usedBalances,
            }),
          profileMetadata,
        )

        // 総平均単価確定後に同じアクションを再生成し、取引の全件保持を避ける。
        // 生成処理が破壊的に変更する loan と cross bundle は前期 DB から読み直す。
        loans = new Map(
          (
            await profiledReadFromDatabase((database) =>
              selectLoans({
                database,
                accountingPeriodId: accountingPeriod.id - 1,
              }),
            )
          ).map((loan) => [loan.id, loan]),
        )
        crossActionBundles = new Map(
          (
            await profiledReadFromDatabase((database) =>
              selectCrossActionBundles({
                database,
                accountingPeriodId: accountingPeriod.id - 1,
                fiatCurrencyList: fiats,
              }),
            )
          ).map((bundle) => [bundle.id, bundle]),
        )
        assetBalances = createAssetBalanceMap(
          cloneAssetBalanceList(previousAssetBalances),
        )
        assetBorrows = createAssetBorrowMap(
          cloneAssetBorrowList(previousAssetBorrows),
        )
        nftAcquiredTimestamps = new Map(previousNftAcquiredTimestamps)
        usedBalances = new Map()
        await runActionBatches({
          reportGenerationProgress: false,
          onTransactions: async ({ transactions, batchMetadata }) => {
            const updatedTransactions: Transaction[] = []
            await effectiveProfiler.measure(
              'period.wacb.calculateTransactionPlByWacb',
              async () => {
                // WACB の破壊的更新を、再生成中の残高や cross bundle から分離する。
                for (const generatedTransaction of transactions) {
                  const transaction = {
                    ...generatedTransaction,
                    ins: generatedTransaction.ins.map((transfer) => ({
                      ...transfer,
                    })),
                    outs: generatedTransaction.outs.map((transfer) => ({
                      ...transfer,
                    })),
                  }
                  const result = await calculateTransactionPlByWacb({
                    transaction,
                    assetBalances: assetBalancesForWacb,
                    assetBorrows: assetBorrowsForWacb,
                    totalBalances,
                  })
                  updatedTransactions.push(result.transaction)
                  assetBalancesForWacb = result.assetBalances
                  assetBorrowsForWacb = result.assetBorrows
                  totalBalances = result.totalBalances

                  if (onUpdateProgress) {
                    onUpdateProgress({
                      synced:
                        (accountingPeriod.end - accountingPeriod.start) / 2 +
                        (transaction.timestamp - accountingPeriod.start) / 2,
                    })
                  }
                }
              },
              batchMetadata,
            )
            recordSummaryTransactions({
              accumulator: summaryAccumulator,
              period: accountingPeriod,
              transactions: updatedTransactions,
            })
            transactionWriteBuffer.push(...updatedTransactions)
            if (
              transactionWriteBuffer.length >=
              effectiveTransactionFlushItemCount
            ) {
              const transactionsToFlush = [...transactionWriteBuffer]
              transactionWriteBuffer.length = 0
              await flushTransactions({
                eventName: 'period.wacb.insertAccountingTransactions',
                transactions: transactionsToFlush,
                metadata: {
                  ...batchMetadata,
                  flushIndex: transactionFlushIndex,
                  transactionCount: transactionsToFlush.length,
                },
              })
              transactionFlushIndex += 1
            }
          },
        })

        await flushTransactions({
          eventName: 'period.wacb.insertAccountingTransactions',
          transactions: transactionWriteBuffer,
          metadata: {
            ...profileMetadata,
            flushIndex: transactionFlushIndex,
            transactionCount: transactionWriteBuffer.length,
            flushType: 'final',
          },
        })
        transactionWriteBuffer.length = 0

        assetBalanceList = [...totalBalances.entries()].map(
          ([asset, balance]) => ({
            asset,
            balances: [balance],
          }),
        )
        assetBorrowList = [...assetBorrowsForWacb.entries()].map(
          ([asset, borrows]) => ({
            asset,
            borrows,
          }),
        )
      } else {
        await flushTransactions({
          eventName: 'period.actionBatch.insertAccountingTransactions',
          transactions: transactionWriteBuffer,
          metadata: {
            ...profileMetadata,
            flushIndex: transactionFlushIndex,
            transactionCount: transactionWriteBuffer.length,
            flushType: 'final',
          },
        })
        transactionWriteBuffer.length = 0
        assetBalanceList = [...assetBalances.entries()].map(
          ([asset, balances]) => ({
            asset,
            balances,
          }),
        )
        assetBorrowList = [...assetBorrows.entries()].map(
          ([asset, borrows]) => ({
            asset,
            borrows,
          }),
        )
      }

      await effectiveProfiler.measure(
        'period.saveAccountingPeriodData',
        async () =>
          await profiledWriteToDatabase((database) =>
            saveAccountingPeriodData({
              database,
              accountingPeriod,
              loans: [...loans.values()],
              crossActionBundles: [...crossActionBundles.values()],
              assetBalances: assetBalanceList,
              assetBorrows: assetBorrowList,
              nftAcquiredTimestamps,
              computedSummary: buildAccountingPeriodComputedSummary({
                accumulator: summaryAccumulator,
              }),
              profiler: effectiveProfiler,
            }),
          ),
        {
          ...profileMetadata,
          actionCount: summaryAccumulator.actionIds.size,
        },
      )
    },
    profileMetadata,
  )
}
